import type { CostCategory, ItemFields } from "./model.ts";

// Money as people read it, to the cent where it has cents: "SGD 609.93", "SGD 600", "¥3,200" (the web's cards and
// Tripmo's reminders alike).
export function paidMoney(amount: number, currency: string | null | undefined): string {
  return sayMoney(amount, currency, CENTS);
}

// Money rounded to the whole unit, for totals and estimates: "SGD 1,673", "¥4,000", "About SGD 600".
export function roundMoney(amount: number, currency: string | null | undefined): string {
  return sayMoney(amount, currency, WHOLE);
}

const CENTS = { trailingZeroDisplay: "stripIfInteger" } as Intl.NumberFormatOptions;
const WHOLE: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };

// Never throws, so no amount can take a page down (DESIGN.md, the log row "Money without an estimate": Intl throws
// `RangeError: Invalid currency code` on "", and Bookings fell over to "Something went wrong"). Stored currencies are
// ISO codes (`Currency`, src/model.ts); anything else, which only a bug can pass, still reads: with no code at all,
// the amount alone ("1,673.31"); with words that aren't a code, those words before it ("S$ 1,673.31").
function sayMoney(amount: number, currency: string | null | undefined, digits: Intl.NumberFormatOptions): string {
  const code = currency?.trim() ?? "";
  if (code) {
    try {
      return new Intl.NumberFormat("en", { ...digits, style: "currency", currency: code }).format(amount);
    } catch {
      // Not a code Intl takes: said as written, below.
    }
  }
  const number = new Intl.NumberFormat("en", { maximumFractionDigits: 2, ...digits }).format(amount);
  return code ? `${code} ${number}` : number;
}

// What the trip has cost so far: everything recorded as paid less refunds received, apart from it what a card on
// file will be charged later (coming up, by date: `charges`), and what's booked but nobody has recorded paying or
// a charge for (a hotel paid at check-in), per currency, and by category (the three together: what's committed).
// Refunds still on their way are kept apart, not taken off yet. Plain sums for display; anything beyond (shares,
// what's left per day) is the AI's maths.

export interface Amount {
  currency: string;
  amount: number;
}

export interface Spending {
  // Paid, less refunds received.
  paid: Amount[];
  // Charged later: what cards on file will be charged, not paid yet (features/bookings-and-costs.md, "Charged
  // later").
  coming_up: Amount[];
  // Refunds received (already taken off paid), and refunds still on their way (not taken off).
  refunded: Amount[];
  refunds_due: Amount[];
  // The price of booked things with no payment or charge recorded.
  booked_unpaid: Amount[];
  // Where it went: paid, coming up and booked-not-paid together.
  by_category: Record<CostCategory, Amount[]>;
}

const CATEGORIES: CostCategory[] = ["stays", "transport", "food", "activities", "other"];

export function categoryOf(fields: Pick<ItemFields, "type" | "cost">): CostCategory {
  if (fields.cost?.category) return fields.cost.category;
  return fields.type === "stay" ? "stays" : fields.type === "move" ? "transport" : "other";
}

type Row = {
  status: string;
  fields: Pick<ItemFields, "type" | "cost" | "paid" | "charges" | "refunds" | "booking">;
};

// What a charge later is still to come on: something called off (cancelled, dropped) or not chosen won't be
// charged, and an option isn't until it's chosen.
const NOT_CHARGED = new Set(["cancelled", "dropped", "option", "not_chosen"]);
export const chargesCount = (status: string) => !NOT_CHARGED.has(status);

// `people` is how many a per-person price is for when the booking doesn't say (the trip's members).
export function spending(rows: Row[], people = 1): Spending {
  const paid = new Map<string, number>();
  const coming = new Map<string, number>();
  const unpaid = new Map<string, number>();
  const refunded = new Map<string, number>();
  const due = new Map<string, number>();
  const byCategory = new Map<CostCategory, Map<string, number>>(CATEGORIES.map((c) => [c, new Map()]));
  const add = (map: Map<string, number>, currency: string, amount: number) =>
    map.set(currency, (map.get(currency) ?? 0) + amount);
  for (const { status, fields } of rows) {
    const category = byCategory.get(categoryOf(fields)) as Map<string, number>;
    for (const p of fields.paid ?? []) {
      add(paid, p.currency, p.amount);
      add(category, p.currency, p.amount);
    }
    const charges = chargesCount(status) ? (fields.charges ?? []) : [];
    for (const c of charges) {
      add(coming, c.currency, c.amount);
      add(category, c.currency, c.amount);
    }
    for (const r of fields.refunds ?? []) {
      if (!r.received) {
        add(due, r.currency, r.amount);
        continue;
      }
      add(refunded, r.currency, r.amount);
      add(paid, r.currency, -r.amount);
      add(category, r.currency, -r.amount);
    }
    if (status === "booked" && !fields.paid?.length && !charges.length && fields.cost) {
      const times = fields.cost.per === "person" ? fields.booking?.for?.length || people : 1;
      add(unpaid, fields.cost.currency, fields.cost.amount * times);
      add(category, fields.cost.currency, fields.cost.amount * times);
    }
  }
  return {
    paid: amounts(paid),
    coming_up: amounts(coming),
    refunded: amounts(refunded),
    refunds_due: amounts(due),
    booked_unpaid: amounts(unpaid),
    by_category: Object.fromEntries(
      CATEGORIES.map((c) => [c, amounts(byCategory.get(c) as Map<string, number>)]),
    ) as Record<CostCategory, Amount[]>,
  };
}

// Largest first, so the trip's main currency leads. A currency that comes to nothing (all of it given
// back) is left out.
function amounts(map: Map<string, number>): Amount[] {
  return [...map]
    .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }))
    .filter((a) => a.amount !== 0)
    .sort((a, b) => b.amount - a.amount || a.currency.localeCompare(b.currency));
}

// A card as its few words say it, its last digits kept with the word before them ("Mastercard 8682" never breaks
// into "Mastercard" and "8682" on two lines: a no-break space).
export const cardWords = (method: string): string => method.replace(/ (\d+)$/, "\u00a0$1");

// A charge still to come, with what it's for: Money's Coming up and get_trip's money list them by date.
export interface ComingCharge {
  on: string;
  item: string;
  title: string;
  by: string;
  amount: number;
  currency: string;
  method?: string;
}

// Every charge still to come, by date (then as recorded), on what can still be charged.
export function comingCharges(
  rows: readonly { id: string; title: string; status: string; fields: Pick<ItemFields, "charges"> }[],
): ComingCharge[] {
  return rows
    .flatMap((r) =>
      chargesCount(r.status)
        ? (r.fields.charges ?? []).map((c) => ({
            on: c.on,
            item: r.id,
            title: r.title,
            by: c.by,
            amount: c.amount,
            currency: c.currency,
            ...(c.method ? { method: c.method } : {}),
          }))
        : [],
    )
    .map((c, n) => ({ c, n }))
    .sort((a, b) => a.c.on.localeCompare(b.c.on) || a.n - b.n)
    .map(({ c }) => c);
}
